import { type ReactNode } from 'react';
import { Icon } from './Icon';

/* ---------- DataTable ---------- */
export interface Column<R> {
  key: string;
  header?: ReactNode;
  align?: 'left' | 'right' | 'center';
  width?: number | string;
  render?: (row: R) => ReactNode;
}

export interface DataTableProps<R> {
  columns?: Column<R>[];
  rows?: R[];
  onRowClick?: (row: R) => void;
}

export function DataTable<R extends { id?: string | number }>({
  columns = [],
  rows = [],
  onRowClick,
}: DataTableProps<R>) {
  return (
    <table className="zen-table">
      <thead>
        <tr>
          {columns.map((c) => (
            <th key={c.key} style={{ textAlign: c.align || 'left', width: c.width }}>
              {c.header}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr
            key={r.id ?? i}
            onClick={onRowClick ? () => onRowClick(r) : undefined}
            style={{ cursor: onRowClick ? 'pointer' : undefined }}
          >
            {columns.map((c) => (
              <td
                key={c.key}
                style={{ textAlign: c.align || 'left' }}
                className={c.align === 'right' ? 'zen-table__num' : undefined}
              >
                {c.render ? c.render(r) : ((r as Record<string, unknown>)[c.key] as ReactNode)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/* ---------- StatCard ---------- */
export interface StatCardProps {
  label: ReactNode;
  value: ReactNode;
  delta?: ReactNode;
  direction?: 'up' | 'down' | 'flat';
  icon?: string;
  footnote?: ReactNode;
}

export function StatCard({ label, value, delta, direction = 'flat', icon, footnote }: StatCardProps) {
  return (
    <div className="zen-card zen-stat">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-4)' }}>
        <span className="zen-stat__label">{label}</span>
        {icon && <Icon name={icon} size="16px" style={{ color: 'var(--navy-300)' }} />}
      </div>
      <div className="zen-stat__value">{value}</div>
      {(delta || footnote) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)' }}>
          {delta && (
            <span className={`zen-stat__delta zen-stat__delta--${direction}`}>
              <Icon name={direction === 'up' ? 'trending-up' : direction === 'down' ? 'trending-down' : 'minus'} size="13px" />
              {delta}
            </span>
          )}
          {footnote && <span style={{ font: 'var(--type-caption)', color: 'var(--text-muted)' }}>{footnote}</span>}
        </div>
      )}
    </div>
  );
}
